import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { companyAPI } from '../../services/api'
import {
  Link2,
  FileText,
  Users,
  BookOpen,
  Layout,
  Globe,
  ExternalLink,
  Loader2
} from 'lucide-react'

export default function Links() {
  const [resourceSections, setResourceSections] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchResourceLinks()
  }, [])

  const fetchResourceLinks = async () => {
    try {
      const response = await companyAPI.getResourceLinks()
      if (response.data.success) {
        setResourceSections(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching resource links:', error)
      // Use fallback data if API fails
      setResourceSections(getFallbackData())
    } finally {
      setLoading(false)
    }
  }

  const getIconComponent = (iconName) => {
    const icons = {
      'link': Link2,
      'file-text': FileText,
      'users': Users,
      'book-open': BookOpen,
      'layout': Layout,
      'globe': Globe
    }
    const IconComponent = icons[iconName] || Link2
    return <IconComponent className="h-5 w-5" />
  }

  const getFallbackData = () => [
    {
      sectionTitle: 'Event Resources',
      sectionIcon: 'link',
      links: [
        {
          title: 'Event Guidelines',
          description: 'Demo presentation rules and format',
          url: '/guidelines'
        },
        {
          title: 'Venue Information',
          description: 'Aurika Mumbai location and facilities',
          url: 'https://maps.google.com'
        },
        {
          title: 'Schedule & Agenda',
          description: 'Complete event timeline',
          url: '#'
        }
      ]
    },
    {
      sectionTitle: 'Platform Access',
      sectionIcon: 'layout',
      links: [
        {
          title: 'Networking Platform',
          description: 'Connect with attendees and investors',
          url: 'https://networking.indiafintech.com'
        },
        {
          title: 'Job Posting Portal',
          description: 'Post openings and find talent',
          url: 'https://jobs.indiafintech.com'
        },
        {
          title: 'Media Center',
          description: 'Download promotional materials',
          url: '#'
        }
      ]
    },
    {
      sectionTitle: 'Documentation',
      sectionIcon: 'file-text',
      links: [
        {
          title: 'Presentation Template',
          description: 'Standard format for demos',
          url: 'https://docs.google.com/presentation'
        },
        {
          title: 'Company Profile Form',
          description: 'Submit your company details',
          url: '/timeline'
        },
        {
          title: 'Technical Requirements',
          description: 'AV and setup specifications',
          url: '#'
        }
      ]
    },
    {
      sectionTitle: 'Community',
      sectionIcon: 'users',
      links: [
        {
          title: 'LinkedIn Group',
          description: 'Join the IFTA community',
          url: 'https://linkedin.com/groups/ifta2025'
        },
        {
          title: 'Slack Channel',
          description: 'Real-time discussions',
          url: 'https://ifta2025.slack.com'
        },
        {
          title: 'Newsletter',
          description: 'Stay updated with latest news',
          url: 'https://indiafintech.com/newsletter'
        }
      ]
    }
  ]

  const handleLinkClick = (url) => {
    if (url.startsWith('http')) {
      window.open(url, '_blank')
    } else if (url !== '#') {
      window.location.href = url
    }
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Important Resources</h1>
        <p className="text-muted-foreground mt-2">
          Essential links and documents for IFTA 2025
        </p>
      </div>

      {/* Resource Links Grid */}
      <div className="grid gap-6 md:grid-cols-2">
        {resourceSections.map((section, index) => (
          <Card key={index} className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2">
                {getIconComponent(section.sectionIcon)}
                <CardTitle className="text-lg">{section.sectionTitle}</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {section.links.map((link, linkIndex) => (
                <button
                  key={linkIndex}
                  onClick={() => handleLinkClick(link.url)}
                  className="w-full text-left group hover:bg-white/50 p-3 rounded-lg transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 group-hover:text-green-700">
                        {link.title}
                      </p>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {link.description}
                      </p>
                    </div>
                    <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-green-600 mt-0.5 flex-shrink-0 ml-2" />
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}